import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import type { JSX } from 'react';
import { NotFoundPage } from '@/components/error-boundary/not-found-page.js';
import { RouteErrorBoundary } from '@/components/error-boundary/route-error-boundary.js';
import { TooltipProvider } from '@/components/ui/tooltip.js';

/**
 * Racine du routeur — définie ici plutôt qu'inline dans `router.tsx` pour que
 * chaque fichier de route (`login.tsx`, `authenticated.tsx`…) puisse importer
 * `rootRoute` sans dépendre de `router.tsx` (qui, lui, assemble l'arbre en
 * important toutes les routes). `getParentRoute: () => rootRoute` étant un
 * thunk, cet import croisé est résolu paresseusement — pas de cycle réel
 * (voir `README-shell.md`).
 */
export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * `TooltipProvider` est monté ici, à la racine du routeur, et non dans
 * `main.tsx` : tout écran qui rend un `Tooltip` doit l'avoir au-dessus de lui,
 * y compris quand l'arbre de routes est monté sans l'entrée de production
 * (tests de parcours, `createAppRouter`).
 */
function RootComponent(): JSX.Element {
  return (
    <TooltipProvider delayDuration={200}>
      <Outlet />
    </TooltipProvider>
  );
}

/**
 * `validateSearch` posé ici, en passthrough (`Record<string, unknown>`),
 * plutôt que laissé absent : TanStack Router calcule le type de recherche
 * *fusionné* de chaque route à partir de tous ses ancêtres. Sans ce
 * passthrough sur la racine, une route qui ne déclare pas son propre
 * `validateSearch` (`_authenticated`, `/`) hérite d'un type de recherche vide
 * et exact — incompatible avec les hooks génériques inter-écrans
 * (`useOriginFilter`, `useDataTableUrlState`, `README-shell.md`) qui lisent et
 * écrivent des clés d'URL arbitraires via `useSearch`/`useNavigate` sans être
 * liés à une route précise.
 */
function passthroughSearch(search: Record<string, unknown>): Record<string, unknown> {
  return search;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorBoundary,
  validateSearch: passthroughSearch,
});
